import React from "react";
import { Typography, Button, Dialog } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AxiosResponse } from "axios";
import { FileUploader } from "../FileUploader";
import { showSnack } from "../../utils/component";
import { useSnackbar } from "notistack";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";

interface UploadCSVDialogProps {
	//Id of product
	productId: string;
	//Method for sending the file to POW OAPI to be processed
	processFile: (file: string, fileName: string, productId: string) => Promise<AxiosResponse<boolean>>;
	//The label for the button.
	label: string;
	//The title of the dialogue
	title: string;
	//used to force the parent component to rerender
	forceUpdate: () => void;
}

const useStyles = makeStyles(() => ({
	appBar: {
		position: "relative",
	},
	toolbar: {
		justifyContent: "space-between",
	},
	dialogTitle: { textAlign: "center", marginTop: 10, marginBottom: 10 },
	fileUploaderWrapper: { textAlign: "center", marginTop: "60px", marginBottom: "60px" },
}));

/**
 * Displays a dialog that prompts the user to uploda a file for processing and then sends that file to the backend api specified through props.
 * @param props
 */
export const UploadCSVDialog = (props: UploadCSVDialogProps) => {
	const [file, setFile] = React.useState<string>("");
	const [fileName, setFileName] = React.useState("");
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = React.useState(false);

	const classes = useStyles();

	const checkAndSetFileName = (newFileName: string) => {
		if (!newFileName.endsWith(".csv")) {
			showSnack(enqueueSnackbar, "The file type is invalid", "error");
		}
		setFileName(newFileName);
	};

	const submit = async () => {
		try {
			const response = await props.processFile(file, fileName, props.productId);
			setOpen(false);
			setFileName("");
			setFile("");
			props.forceUpdate();
			const result = response.data;
			if (result) {
				showSnack(enqueueSnackbar, "The file was successfully processed.", "success");
			} else {
				showSnack(enqueueSnackbar, "An error occured while processing the file.", "error");
			}
		} catch (ex) {
			console.log(ex);
		}
	};

	return (
		<div>
			<Button
				size="small"
				data-testid="full-screen-dialog-open-btn"
				variant="outlined"
				color="primary"
				onClick={() => setOpen(true)}
			>
				{props.label}
			</Button>
			<Dialog
				fullWidth={true}
				data-testid="csv-upload-drawer-container"
				open={open}
				onClose={() => {
					setOpen(false);
					setFileName("");
					setFile("");
				}}
			>
				<AppBar color="default" className={classes.appBar}>
					<Toolbar className={classes.toolbar}>
						<IconButton edge="start" color="inherit" size="small" onClick={() => setOpen(false)} aria-label="close">
							<CloseIcon />
						</IconButton>
						<Typography variant="h6" className={classes.dialogTitle}>
							{props.title}
						</Typography>

						<Button
							size="small"
							data-testid="process-file-btn"
							variant="contained"
							color="primary"
							disabled={!fileName.endsWith(".csv")}
							onClick={submit}
						>
							Process File
						</Button>
					</Toolbar>
				</AppBar>

				<div className={classes.fileUploaderWrapper}>
					<FileUploader fileName={fileName} setFile={setFile} setFileName={checkAndSetFileName}></FileUploader>
				</div>
			</Dialog>
		</div>
	);
};
